import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  Cog6ToothIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  FolderIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  BeakerIcon,
  UserGroupIcon,
  SparklesIcon,
  BoltIcon,
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../components/dashboard/hse/HsePageChrome";
import { HseTabBar } from "../../../components/dashboard/hse/HseTabBar";
import { HsePageBody } from "../../../components/dashboard/hse/HsePageBody";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import RoleOnly, { useUserHasAccess } from "../../../routes/roleOnly";
import { getDisciplines, loadDisciplineToCompany } from "../../../api/hse/disciplines";
import ChecklistTypesStacklist from "../../../components/dashboard/quality/settings/checklist-types-stacklist";
import { DeviationNotificationSettingsForm } from "../../../components/dashboard/quality/deviations/DeviationNotificationSettingsForm";
import { HseDigestSettingsForm } from "../../../components/dashboard/hse/HseDigestSettingsForm";
import { HseModuleSettingsForm } from "../../../components/dashboard/hse/HseModuleSettingsForm";
import { ChemicalInventorySettingsForm } from "../../../components/dashboard/hse-ks/ChemicalInventorySettingsForm";
import { HseSafetyRepSettingsForm } from "../../../components/dashboard/hse/HseSafetyRepSettingsForm";
import { HseRiskSettingsForm } from "../../../components/dashboard/hse/HseRiskSettingsForm";
import { HseAiSettingsForm } from "../../../components/dashboard/hse/HseAiSettingsForm";
import { HseSetupMissingBanner } from "../../../components/dashboard/hse/HseSetupMissingBanner";

const SETTINGS_TABS = [
  "module",
  "safety_rep",
  "risk",
  "ai",
  "disciplines",
  "checklist_types",
  "chemical_inventory",
  "deviation_notifications",
  "hse_digest",
];
const ADMIN_SETTINGS_TABS = ["deviation_notifications", "hse_digest"];

/**
 * HSE/KS Settings - Innstillinger for HMS & KS modulen
 */
export const HseKsSettings = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const initialTab = SETTINGS_TABS.includes(requestedTab)
    ? requestedTab
    : "module";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [expandedDisciplines, setExpandedDisciplines] = useState(new Set());

  const canManage = useUserHasAccess({ role: "hse_ks_settings", action: "update" });
  const isAdmin = useUserHasAccess({ role: "admin" });

  const [availableDisciplines, setAvailableDisciplines] = useState([]);
  const [companyDisciplines, setCompanyDisciplines] = useState([]);

  // Fetch disciplines on mount
  useEffect(() => {
    fetchDisciplines();
  }, []);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (ADMIN_SETTINGS_TABS.includes(tab) && !isAdmin) {
      setActiveTab("module");
      return;
    }
    setActiveTab(SETTINGS_TABS.includes(tab) ? tab : "module");
  }, [searchParams, isAdmin]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const next = new URLSearchParams(searchParams);
    if (tabId === "module") {
      next.delete("tab");
    } else {
      next.set("tab", tabId);
    }
    setSearchParams(next, { replace: true });
  };

  const fetchDisciplines = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDisciplines();
      const templates = response.templates || [];
      const companyDisciplines = response.company_disciplines || [];
      
      // Create a map of loaded discipline codes for quick lookup
      const loadedCodes = new Set(companyDisciplines.map(d => d.code));
      
      // Mark templates as loaded if they exist in company disciplines
      const templatesWithStatus = templates.map(template => ({
        ...template,
        isLoaded: loadedCodes.has(template.code),
        companyDisciplineId: companyDisciplines.find(d => d.code === template.code)?.id
      }));
      
      setAvailableDisciplines(templatesWithStatus);
      setCompanyDisciplines(companyDisciplines);
    } catch (err) {
      console.error("Error fetching disciplines:", err);
      setError(true);
      toast.error(t("Kunne ikke laste fag/bransjer"));
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if discipline is loaded
  const isDisciplineLoaded = (disciplineCode) => {
    return companyDisciplines.some((d) => d.code === disciplineCode);
  };

  // Old test data - removed, now using API data
  const [oldTestData] = useState([
    {
      id: "prosjektering_vvs",
      name: "Prosjektering VVS",
      description: "Dokumentasjon og prosedyrer for VVS-prosjektering",
      loaded: true,
      loadedAt: "2024-01-15",
      categories: [
        { id: "hms", name: "HMS", chapters: 10 },
        { id: "ks", name: "Kvalitetssystem", chapters: 8 },
        { id: "mil", name: "Miljøstyring", chapters: 6 },
        { id: "lov", name: "Lover og Forskrifter", chapters: 4 },
        { id: "kom", name: "Kompetanse", chapters: 3 },
      ],
    },
    {
      id: "utførende_ventilasjon",
      name: "Utførende ventilasjon",
      description: "Dokumentasjon og prosedyrer for utførende ventilasjonsarbeid",
      loaded: false,
      categories: [
        { id: "hms", name: "HMS", chapters: 12 },
        { id: "ks", name: "Kvalitetssystem", chapters: 9 },
        { id: "mil", name: "Miljøstyring", chapters: 7 },
        { id: "lov", name: "Lover og Forskrifter", chapters: 5 },
        { id: "kom", name: "Kompetanse", chapters: 4 },
      ],
    },
    {
      id: "prosjektering_elektro",
      name: "Prosjektering Elektro",
      description: "Dokumentasjon og prosedyrer for elektro-prosjektering",
      loaded: false,
      categories: [
        { id: "hms", name: "HMS", chapters: 11 },
        { id: "ks", name: "Kvalitetssystem", chapters: 8 },
        { id: "mil", name: "Miljøstyring", chapters: 5 },
        { id: "lov", name: "Lover og Forskrifter", chapters: 4 },
        { id: "kom", name: "Kompetanse", chapters: 3 },
      ],
    },
    {
      id: "utførende_elektro",
      name: "Utførende Elektro",
      description: "Dokumentasjon og prosedyrer for utførende elektroarbeid",
      loaded: false,
      categories: [
        { id: "hms", name: "HMS", chapters: 13 },
        { id: "ks", name: "Kvalitetssystem", chapters: 10 },
        { id: "mil", name: "Miljøstyring", chapters: 6 },
        { id: "lov", name: "Lover og Forskrifter", chapters: 5 },
        { id: "kom", name: "Kompetanse", chapters: 4 },
      ],
    },
  ]);

  const tabs = [
    { id: "module", label: t("hse.settings.hub_tab"), icon: ShieldCheckIcon },
    { id: "safety_rep", label: t("hse.settings.safety_rep.hub_tab"), icon: UserGroupIcon },
    { id: "risk", label: t("hse.settings.risk.hub_tab"), icon: BoltIcon },
    { id: "ai", label: t("hse.settings.ai.hub_tab"), icon: SparklesIcon },
    { id: "disciplines", label: t("Fag/Bransje"), icon: BuildingOfficeIcon },
    { id: "checklist_types", label: t("Sjekklistetyper"), icon: ClipboardDocumentListIcon },
    { id: "chemical_inventory", label: t("chemical.inventory"), icon: BeakerIcon },
    ...(isAdmin
      ? [
          {
            id: "deviation_notifications",
            label: t("deviation.settings.hub_tab"),
            icon: ExclamationTriangleIcon,
          },
          {
            id: "hse_digest",
            label: t("hse.digest.hub_tab"),
            icon: EnvelopeIcon,
          },
        ]
      : []),
  ];

  const toggleDiscipline = (disciplineId) => {
    const newExpanded = new Set(expandedDisciplines);
    if (newExpanded.has(disciplineId)) {
      newExpanded.delete(disciplineId);
    } else {
      newExpanded.add(disciplineId);
    }
    setExpandedDisciplines(newExpanded);
  };

  const handleLoadDiscipline = async (disciplineId, isUpdate = false) => {
    try {
      setLoading(true);
      const response = await loadDisciplineToCompany(disciplineId);
      
      if (isUpdate) {
        toast.success(t("Fag/bransje oppdatert"));
      } else {
        toast.success(t("Fag/bransje lastet inn"));
      }
      
      // Refresh disciplines list
      await fetchDisciplines();
    } catch (err) {
      console.error("Error loading/updating discipline:", err);
      const errorMessage = err.response?.data?.errors?.[0] || 
        (isUpdate ? t("Kunne ikke oppdatere fag/bransje") : t("Kunne ikke laste inn fag/bransje"));
      toast.error(errorMessage);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Note: Unloading disciplines is not implemented as per requirements
  // Users must manually delete chapters/categories if needed

  const renderDisciplineStructure = (discipline) => {
    const isExpanded = expandedDisciplines.has(discipline.id || discipline.code);
    const loaded = discipline.isLoaded || isDisciplineLoaded(discipline.code);
    const totalChapters = discipline.categories?.reduce((sum, cat) => {
      return sum + (cat.chapters?.length || 0);
    }, 0) || 0;

    return (
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <button
                onClick={() => toggleDiscipline(discipline.id || discipline.code)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                )}
              </button>
              <BuildingOfficeIcon className="h-6 w-6 text-blue-500" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {discipline.name}
                </h3>
                {discipline.description && (
                  <p className="text-sm text-gray-500 mt-1">
                    {discipline.description}
                  </p>
                )}
              </div>
            </div>

            {isExpanded && discipline.categories && (
              <div className="ml-10 mt-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {discipline.categories.map((category) => (
                    <div
                      key={category.id || category.code}
                      className="flex items-center space-x-2 p-2 bg-gray-50 rounded border border-gray-200"
                    >
                      <FolderIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {category.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {category.chapters?.length || 0} {t("kapitler")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    <strong>{t("Totalt")}:</strong> {discipline.categories.length} {t("kategorier")}, {totalChapters} {t("kapitler")}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="ml-4 flex items-center space-x-2">
            {loaded ? (
              <>
                <div className="flex items-center space-x-1 text-green-600">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">{t("Lastet inn")}</span>
                </div>
                {canManage && (
                  <button
                    onClick={() => handleLoadDiscipline(discipline.id || discipline.code, true)}
                    disabled={loading}
                    className="ml-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                    title={t("Oppdater fra template")}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    <span>{t("Oppdater")}</span>
                  </button>
                )}
              </>
            ) : (
              canManage && (
                <button
                  onClick={() => handleLoadDiscipline(discipline.id || discipline.code, false)}
                  disabled={loading}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>{t("Last inn")}</span>
                </button>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <HsePageChrome
      title={t("Innstillinger")}
      subtitle={t("Administrer innstillinger for HMS & KS modulen")}
    >
      <HsePageBody>
        <HseSetupMissingBanner />
        <HseTabBar
          wrap
          tabs={tabs}
          activeId={activeTab}
          onSelect={(tab) => handleTabChange(tab.id)}
          ariaLabel={t("hse.settings.title")}
        />

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === "module" && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  {t("hse.settings.title")}
                </h3>
                <p className="text-sm text-blue-700">
                  {t("hse.settings.hub_intro")}
                </p>
              </div>
              <HseModuleSettingsForm />
            </div>
          )}

          {activeTab === "safety_rep" && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  {t("hse.settings.safety_rep.title")}
                </h3>
                <p className="text-sm text-blue-700">
                  {t("hse.settings.safety_rep.hub_intro")}
                </p>
              </div>
              <HseSafetyRepSettingsForm />
            </div>
          )}

          {activeTab === "risk" && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  {t("hse.settings.risk.title")}
                </h3>
                <p className="text-sm text-blue-700">
                  {t("hse.settings.risk.hub_intro")}
                </p>
              </div>
              <HseRiskSettingsForm />
            </div>
          )}

          {activeTab === "ai" && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  {t("hse.settings.ai.title")}
                </h3>
                <p className="text-sm text-blue-700">
                  {t("hse.settings.ai.hub_intro")}
                </p>
              </div>
              <HseAiSettingsForm />
            </div>
          )}

          {activeTab === "disciplines" && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  {t("Fag/Bransje")}
                </h3>
                <p className="text-sm text-blue-700">
                  {t("Velg og last inn fag/bransjer for å aktivere dokumentstrukturen. Hver fag/bransje inneholder kategorier og kapitler som kan brukes i dokumenthåndteringen.")}
                </p>
              </div>

              {loading ? (
                <ResourceLoading size="base" />
              ) : error ? (
                <ResourceError errors={[t("Kunne ikke laste fag/bransjer")]} />
              ) : availableDisciplines.length === 0 ? (
                <ResourceEmpty message={t("Ingen fag/bransjer tilgjengelig")} />
              ) : (
                <div className="space-y-4">
                  {availableDisciplines.map((discipline) => (
                    <div key={discipline.id || discipline.code}>
                      {renderDisciplineStructure(discipline)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "checklist_types" && (
            <ChecklistTypesStacklist />
          )}

          {activeTab === "chemical_inventory" && (
            <ChemicalInventorySettingsForm />
          )}

          {activeTab === "deviation_notifications" && isAdmin && (
            <RoleOnly role="admin">
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">
                    {t("deviation.settings.title")}
                  </h3>
                  <p className="text-sm text-blue-700">
                    {t("deviation.settings.hub_intro")}
                  </p>
                </div>
                <DeviationNotificationSettingsForm />
              </div>
            </RoleOnly>
          )}

          {activeTab === "hse_digest" && isAdmin && (
            <RoleOnly role="admin">
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">
                    {t("hse.digest.title")}
                  </h3>
                  <p className="text-sm text-blue-700">
                    {t("hse.digest.hub_intro")}
                  </p>
                </div>
                <HseDigestSettingsForm />
              </div>
            </RoleOnly>
          )}
        </div>
      </HsePageBody>
    </HsePageChrome>
  );
};
